import type { Project } from '@finsim/model';
import { createStore, del, get, set, type UseStore } from 'idb-keyval';

/**
 * Local-first project storage in IndexedDB (docs/PLAN.md §3.5). Works for anonymous users and
 * doubles as the offline cache and outbox for sync when signed in (M7). Values are plain JSON; they are validated and
 * migrated with `parseProject` on load, never trusted.
 */
export interface ProjectMeta {
  id: string;
  name: string;
  updatedAt: number;
  scenarioCount: number;
}

/**
 * How a local project relates to the account (M7). `ownerId` null = local only (never uploaded).
 * `remoteVersion` is the server version the local copy is based on; `dirty` = local edits not
 * yet uploaded.
 */
export interface SyncMeta {
  ownerId: string | null;
  remoteVersion: number | null;
  dirty: boolean;
}

export const LOCAL_ONLY: SyncMeta = { ownerId: null, remoteVersion: null, dirty: false };

export interface LocalProjectStore {
  list: () => Promise<ProjectMeta[]>;
  load: (id: string) => Promise<unknown>;
  /** Saves the project and returns the updated index (most recent first). */
  save: (project: Project, now: number) => Promise<ProjectMeta[]>;
  remove: (id: string) => Promise<ProjectMeta[]>;
  /** Undefined when the project has no sync record yet. */
  getSync: (id: string) => Promise<SyncMeta | undefined>;
  setSync: (id: string, meta: SyncMeta) => Promise<void>;
}

const INDEX_KEY = 'index';
const projectKey = (id: string): string => `project:${id}`;
const syncKey = (id: string): string => `sync:${id}`;

function isSyncMeta(value: unknown): value is SyncMeta {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    (v.ownerId === null || typeof v.ownerId === 'string') &&
    (v.remoteVersion === null || typeof v.remoteVersion === 'number') &&
    typeof v.dirty === 'boolean'
  );
}

function isMeta(value: unknown): value is ProjectMeta {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && typeof v.name === 'string' && typeof v.updatedAt === 'number';
}

export function createLocalProjectStore(
  db: UseStore = createStore('finsim', 'projects'),
): LocalProjectStore {
  const readIndex = async (): Promise<ProjectMeta[]> => {
    const raw: unknown = await get(INDEX_KEY, db);
    return Array.isArray(raw) ? raw.filter(isMeta) : [];
  };
  const byRecency = (a: ProjectMeta, b: ProjectMeta): number => b.updatedAt - a.updatedAt;

  return {
    list: async () => (await readIndex()).sort(byRecency),
    load: (id) => get(projectKey(id), db),
    async save(project, now) {
      await set(projectKey(project.id), project, db);
      const meta: ProjectMeta = {
        id: project.id,
        name: project.name,
        updatedAt: now,
        scenarioCount: project.scenarios.length,
      };
      const index = [meta, ...(await readIndex()).filter((m) => m.id !== project.id)].sort(
        byRecency,
      );
      await set(INDEX_KEY, index, db);
      return index;
    },
    async remove(id) {
      await del(projectKey(id), db);
      await del(syncKey(id), db);
      const index = (await readIndex()).filter((m) => m.id !== id);
      await set(INDEX_KEY, index, db);
      return index;
    },
    async getSync(id) {
      const raw: unknown = await get(syncKey(id), db);
      return isSyncMeta(raw) ? raw : undefined;
    },
    setSync: (id, meta) => set(syncKey(id), meta, db),
  };
}
